import { Injectable } from '@angular/core';
import { CameraSource } from '@capacitor/camera';
import { ActionSheetController } from '@ionic/angular';
import { PlatformDetectorService } from './photo/platform-detector.service';
import { WebPhotoPickerStrategy } from './photo/strategies/web-photo-picker.strategy';
import { NativePhotoPickerStrategy } from './photo/strategies/native-photo-picker.strategy';
import { PhotoPickerStrategy } from './photo/photo-picker.types';

/**
 * Service for picking photos from camera or library.
 * Uses platform-specific strategies to handle differences between web and native.
 */
@Injectable({
    providedIn: 'root'
})
export class PhotoService {
    private readonly strategy: PhotoPickerStrategy;

    constructor(
        private readonly actionSheetCtrl: ActionSheetController,
        private readonly platformDetector: PlatformDetectorService,
        private readonly webStrategy: WebPhotoPickerStrategy,
        private readonly nativeStrategy: NativePhotoPickerStrategy
    ) {
        // Select the appropriate strategy based on platform
        this.strategy = this.platformDetector.isNativePlatform()
            ? this.nativeStrategy
            : this.webStrategy;
    }

    /**
     * Opens a photo selector.
     * On native: Shows action sheet to choose between camera and library.
     * On web: Opens the file picker directly.
     */
    public async openPhotoSelector(
        onSuccess: (fileName: string, base64: string) => void,
        onError?: (error: Error) => void
    ): Promise<void> {
        if (this.platformDetector.isNativePlatform()) {
            await this.showNativeActionSheet(onSuccess, onError);
        } else {
            await this.pickPhotoWithStrategy(CameraSource.Photos, onSuccess, onError);
        }
    }

    private async showNativeActionSheet(
        onSuccess: (fileName: string, base64: string) => void,
        onError?: (error: Error) => void
    ): Promise<void> {
        const actionSheet = await this.actionSheetCtrl.create({
            header: 'Selecteer een afbeelding',
            buttons: [
                {
                    text: 'Camera',
                    handler: () => {
                        this.pickPhotoWithStrategy(CameraSource.Camera, onSuccess, onError);
                    }
                },
                {
                    text: 'Bibliotheek',
                    handler: () => {
                        this.pickPhotoWithStrategy(CameraSource.Photos, onSuccess, onError);
                    }
                },
                {
                    text: 'Annuleren',
                    role: 'destructive'
                }
            ]
        });
        await actionSheet.present();
    }

    private async pickPhotoWithStrategy(
        source: CameraSource,
        onSuccess: (fileName: string, base64: string) => void,
        onError?: (error: Error) => void
    ): Promise<void> {
        try {
            const result = await this.strategy.pickPhoto(source);

            if (result) {
                onSuccess(result.fileName, result.base64Data);
            }
            // If result is null, user cancelled - no action needed
        } catch (error) {
            if (onError) {
                onError(error instanceof Error ? error : new Error(String(error)));
            }
        }
    }
}
